import {
    BILLS_EMAIL_DOMAIN,
    INVOICES_EMAIL_DOMAIN,
    RECEIPTS_EMAIL_DOMAIN,
    WORKSPACE_EMAIL_ALIAS_KINDS,
    WorkspaceEmailAliasKind,
} from "@common/constants/domains/receipts/constants";

const FINAL_RECEIPTS_EMAIL_DOMAIN =
    globalThis.process?.env?.RECEIPTS_EMAIL_DOMAIN ?? RECEIPTS_EMAIL_DOMAIN;
const FINAL_BILLS_EMAIL_DOMAIN =
    globalThis.process?.env?.BILLS_EMAIL_DOMAIN ?? BILLS_EMAIL_DOMAIN;
const FINAL_INVOICES_EMAIL_DOMAIN =
    globalThis.process?.env?.INVOICES_EMAIL_DOMAIN ?? INVOICES_EMAIL_DOMAIN;

const ALIAS_KIND_PRIMARY_DOMAIN: Record<WorkspaceEmailAliasKind, string> = {
    receipts: FINAL_RECEIPTS_EMAIL_DOMAIN,
    bills: FINAL_BILLS_EMAIL_DOMAIN,
    invoices: FINAL_INVOICES_EMAIL_DOMAIN,
};

const ALIAS_EMAIL_DOMAINS = Object.values(ALIAS_KIND_PRIMARY_DOMAIN);

export interface ParsedWorkspaceEmailAliasAddress {
    emailLocalPart: string;
    domain: string;
}

interface DoesAliasDomainMatchKindParams {
    domain: string;
    kind: WorkspaceEmailAliasKind;
}

export function parseWorkspaceEmailAliasAddress(
    emailOrLocalPart: string,
): ParsedWorkspaceEmailAliasAddress | null {
    const trimmed = emailOrLocalPart.trim();
    if (!trimmed.length) {
        return null;
    }

    const atIndex = trimmed.lastIndexOf("@");
    if (atIndex === -1) {
        return null;
    }

    const emailLocalPart = trimmed.slice(0, atIndex);
    const domain = trimmed.slice(atIndex + 1).toLowerCase();

    if (!emailLocalPart.length || !domain.length) {
        return null;
    }

    return { emailLocalPart, domain };
}

export function stripWorkspaceEmailAliasDomain(
    emailOrLocalPart: string,
): string {
    const trimmed = emailOrLocalPart.trim();
    const parsed = parseWorkspaceEmailAliasAddress(trimmed);

    if (parsed) {
        return parsed.emailLocalPart;
    }

    return trimmed;
}

export function isKnownWorkspaceEmailAliasDomain(domain: string): boolean {
    const normalized = domain.toLowerCase();
    return ALIAS_EMAIL_DOMAINS.some(
        (knownDomain) => knownDomain.toLowerCase() === normalized,
    );
}

export function getWorkspaceEmailAliasKindForDomain(
    domain: string,
): WorkspaceEmailAliasKind | null {
    const normalized = domain.toLowerCase();
    return (
        WORKSPACE_EMAIL_ALIAS_KINDS.find(
            (kind) =>
                ALIAS_KIND_PRIMARY_DOMAIN[kind].toLowerCase() === normalized,
        ) ?? null
    );
}

export function doesAliasDomainMatchKind({
    domain,
    kind,
}: DoesAliasDomainMatchKindParams): boolean {
    return (
        ALIAS_KIND_PRIMARY_DOMAIN[kind].toLowerCase() === domain.toLowerCase()
    );
}

interface FormatWorkspaceEmailAliasAddressParams {
    emailLocalPart: string;
    kind: WorkspaceEmailAliasKind;
}

export function formatWorkspaceEmailAliasAddress({
    emailLocalPart,
    kind,
}: FormatWorkspaceEmailAliasAddressParams): string {
    return `${emailLocalPart}@${getWorkspaceEmailAliasDomain(kind)}`;
}

export function getWorkspaceEmailAliasDomain(
    kind: WorkspaceEmailAliasKind,
): string {
    return ALIAS_KIND_PRIMARY_DOMAIN[kind];
}

interface BuildCounterpartyEmailAliasLocalPartParams {
    vendorSlug: string;
    token: string;
}

const MAX_EMAIL_LOCAL_PART_LENGTH = 64;

export function buildCounterpartyEmailAliasLocalPart({
    vendorSlug,
    token,
}: BuildCounterpartyEmailAliasLocalPartParams): string {
    const normalizedToken = token.trim();
    const availableSlugLength =
        MAX_EMAIL_LOCAL_PART_LENGTH - normalizedToken.length - ".".length;
    const normalizedSlug = vendorSlug
        .trim()
        .slice(0, Math.max(availableSlugLength, 0))
        .replace(/-+$/, "");

    if (!normalizedSlug.length) {
        return normalizedToken;
    }

    return `${normalizedSlug}.${normalizedToken}`;
}

interface BuildEntityCounterpartyEmailLocalPartParams {
    customName: string;
    entitySlug: string;
}

const CUSTOM_NAME_PATTERN = /^[a-z0-9]+$/;

export function buildEntityCounterpartyEmailLocalPart({
    customName,
    entitySlug,
}: BuildEntityCounterpartyEmailLocalPartParams): string {
    const normalizedCustomName = customName.trim().toLowerCase();
    const normalizedSlug = entitySlug.trim().toLowerCase();

    if (!CUSTOM_NAME_PATTERN.test(normalizedCustomName)) {
        throw new Error("Custom name must be lowercase letters and digits");
    }
    if (!normalizedSlug.length) {
        throw new Error("Entity slug is required");
    }

    const localPart = `${normalizedCustomName}.${normalizedSlug}`;
    if (localPart.length > MAX_EMAIL_LOCAL_PART_LENGTH) {
        throw new Error("Custom name does not fit the email local part");
    }

    return localPart;
}
